/**
 * MOTOR AGENDADO (A/B) — a troca de vídeo acontece NA HORA marcada, com o próximo já pronto.
 *
 * <p><b>Por que existe.</b> O motor reativo toca e depois corrige: o vídeo começa quando o
 * anterior acaba, descobre o quanto atrasou (tempo de abrir o decoder, primeiro frame) e
 * compensa com seek, velocidade, cooldown, histerese, lead fixo por plataforma… Cada
 * remendo reduz o erro; nenhum zera, porque a causa é o modelo. Medido no grupo 15
 * (out/2026): resíduo de dezenas a centenas de ms entre players, diferente por aparelho.</p>
 *
 * <p><b>O modelo novo.</b> O player já sabe a época e as durações — logo sabe a hora exata, no
 * relógio do servidor, em que CADA troca vai acontecer. Então:</p>
 * <ol>
 *   <li>{@link SCHEDULED_PREPARE_LEAD_MS} antes de T, o próximo vídeo é carregado num 2º
 *   elemento (standby), com o primeiro frame decodificado, pausado e invisível.</li>
 *   <li>Em T, no mesmo frame de tela: standby.play() + troca de papéis (o standby vira o
 *   elemento ativo, o antigo vira standby). Latência de início ≈ 1 frame.</li>
 *   <li>Dentro do item não há o que corrigir: o início foi exato e o vídeo roda a 1,0×.</li>
 * </ol>
 *
 * <p><b>Onde vale.</b> Android, embedded, browser e desktop. Tizen/webOS ficam no AVPlay de 2
 * players, que já é este modelo no plano de HW. Custo: dois decoders ativos por ~2s a cada
 * troca — por isso é flag (`VITE_PLAYBACK_ENGINE=scheduled`) e tem o motor reativo como
 * fallback em tudo que ele não cobre (imagem, zona, gatilho, loop de 1 item, velocidade ≠ 1).</p>
 *
 * <p>Este módulo é a parte PURA (testável): quem liga e quando trocar. O swap em si vive no
 * Stream, que é dono dos elementos.</p>
 */

/** Quanto antes de T o standby começa a carregar o próximo vídeo. */
export const SCHEDULED_PREPARE_LEAD_MS = 2_000;

/** Folga do timer grosso antes de T; o ajuste fino é por rAF até o instante exato. */
export const SCHEDULED_SWITCH_COARSE_LEAD_MS = 40;

/** Um fim de slot até isto no passado ainda é "o fim do slot atual" (troca atrasada), não o da próxima volta. */
const LATE_WINDOW_MS = 5_000;

export function isScheduledEngineEnabled(
  platform: string,
  envFlag: string | undefined = import.meta.env.VITE_PLAYBACK_ENGINE,
): boolean {
  if (platform === 'tizen' || platform === 'webos') return false;
  return envFlag === 'scheduled';
}

export interface NextSwitchPlan {
  /** Índice do item que entra em T. */
  nextIndex: number;
  /** Instante (relógio do servidor, ms) em que o item ATUAL termina e o próximo entra. */
  switchAtMs: number;
  /** Duração do loop inteiro, em ms. */
  loopTotalMs: number;
}

/**
 * Quando o item em `currentIndex` termina, segundo a linha do tempo canônica.
 *
 * <p>É a MESMA régua de `resolveLoopTarget`: `(agora − época) mod loop`. Aqui, em vez de
 * perguntar "onde devo estar agora", perguntamos "a que horas o slot atual acaba": início da
 * volta atual + soma das durações até o fim do slot. Se o player estiver atrás da régua, T já
 * passou e o chamador troca imediatamente; se estiver à frente, T está no futuro — o pulso
 * continua sendo quem corrige desvios grandes de índice.</p>
 *
 * @returns null sem época, loop vazio ou playlist de 1 item (não há o que trocar)
 */
export function resolveNextSwitch(p: {
  playbackStartedAt: string;
  durations: number[];
  currentIndex: number;
  nowMs: number;
}): NextSwitchPlan | null {
  const epochMs = Date.parse(p.playbackStartedAt);
  if (!Number.isFinite(epochMs)) return null;
  const durations = p.durations.map((d) => (d > 0 ? d : 0));
  if (durations.length < 2) return null;
  const totalS = durations.reduce((a, b) => a + b, 0);
  if (totalS <= 0) return null;
  if (p.currentIndex < 0 || p.currentIndex >= durations.length) return null;

  const loopTotalMs = totalS * 1000;
  const lap = Math.floor((p.nowMs - epochMs) / loopTotalMs);
  const loopStartMs = epochMs + lap * loopTotalMs;
  let endOfCurrentS = 0;
  for (let i = 0; i <= p.currentIndex; i++) endOfCurrentS += durations[i];
  const nestaVolta = loopStartMs + endOfCurrentS * 1000;
  // Logo após a virada do loop, o slot atual pode ser o ÚLTIMO da volta anterior: o fim dele
  // foi há instantes (na volta passada), não daqui a uma volta inteira. Se esse fim está a
  // menos de LATE_WINDOW_MS atrás, é ele — o chamador troca já.
  const voltaPassada = nestaVolta - loopTotalMs;
  const switchAtMs =
    voltaPassada >= p.nowMs - LATE_WINDOW_MS ? voltaPassada : nestaVolta;
  return {
    nextIndex: (p.currentIndex + 1) % durations.length,
    switchAtMs,
    loopTotalMs,
  };
}

/** Quantos ms faltam pra T (≥ 0). */
export function msUntil(switchAtMs: number, nowMs: number): number {
  return Math.max(0, switchAtMs - nowMs);
}
